/**
 * The form ↔ wire conversion for call pricing, kept out of the component so the
 * money conversions are testable on their own.
 *
 * TWO UNITS, and they are not converted into each other:
 *
 *   * the CALLER's price is COINS per minute — stored as coins, entered as coins,
 *     so nothing converts and nothing can drift with the coin rate;
 *   * the LISTENER's earning is real money — stored as PAISE, entered as RUPEES,
 *     so exactly one conversion happens, at this edge.
 *
 * Neither is derived from the other and no commission is computed between them.
 */

export interface ModePricing {
  videoCoins: number;
  videoEarnPaise: number;
  audioCoins: number;
  audioEarnPaise: number;
}
export interface TierCallPricing {
  direct: ModePricing;
  random: ModePricing;
}
export interface CallPricingConfig {
  peer: TierCallPricing;
  coach: TierCallPricing;
  clinical: TierCallPricing;
}

/** The call types on the wire (both still stored and read). */
export const CALL_TYPES = ['direct', 'random'] as const;
export type CallType = (typeof CALL_TYPES)[number];

/**
 * The call types the page EDITS. Random calls (Quick Connect) are retired — a
 * direct call now falls back to the next buddy automatically, always at the
 * direct price — so the random row is no longer shown or written. Its stored
 * values are left exactly as they are (history and old rows still read them).
 */
export const EDITABLE_CALL_TYPES = ['direct'] as const;

export const PAISE_PER_RUPEE = 100;

/** Paise → rupees for display (may be fractional, e.g. 433 paise = ₹4.33). */
export const toRupees = (paise: number) => paise / PAISE_PER_RUPEE;
/** Rupees → integer paise for the wire (₹7.80 → 780). */
export const toPaise = (rupees: number | undefined) =>
  Math.round((rupees ?? 0) * PAISE_PER_RUPEE);

/**
 * Flat form shape, one key per input:
 *   `${callType}_pv` / `_pa` — caller price, in COINS/min
 *   `${callType}_ev` / `_ea` — listener earning, in RUPEES/min
 */
export type FormShape = Record<string, number>;

/** Server config → form values, for the tier the page edits. */
export function formValuesFromConfig(
  cfg: CallPricingConfig,
  tier: keyof CallPricingConfig = 'peer',
): FormShape {
  const v: FormShape = {};
  for (const type of CALL_TYPES) {
    const c = cfg[tier][type];
    // Coins arrive as coins — no conversion, so the operator always sees exactly
    // the number that is stored. Only the ₹ side converts.
    v[`${type}_pv`] = c.videoCoins;
    v[`${type}_ev`] = toRupees(c.videoEarnPaise);
    v[`${type}_pa`] = c.audioCoins;
    v[`${type}_ea`] = toRupees(c.audioEarnPaise);
  }
  return v;
}

/** Form values → the PATCH body, for the tier the page edits. */
export function patchBodyFromForm(
  v: FormShape,
  tier: keyof CallPricingConfig = 'peer',
): Partial<Record<keyof CallPricingConfig, TierCallPricing>> {
  const cell = (type: CallType): ModePricing => ({
    videoCoins: Math.round(v[`${type}_pv`] ?? 0),
    videoEarnPaise: toPaise(v[`${type}_ev`]),
    audioCoins: Math.round(v[`${type}_pa`] ?? 0),
    audioEarnPaise: toPaise(v[`${type}_ea`]),
  });
  // Only the tier and call type this page edits. A PATCH is a subset all the way
  // down, so untouched tiers — and the retired random cells — keep their stored
  // values.
  return { [tier]: { direct: cell('direct') } } as Partial<Record<keyof CallPricingConfig, TierCallPricing>>;
}
